import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, ShieldCheck, Clock, BadgeCheck, ArrowRight, MapPin, Star, Zap, MessageSquareQuote } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { NL_IMAGE, RIVIERA_HERO, RIVIERA_CITIES, NL_AREAS, rivieraCityLink } from "@/lib/riviera";
import MarketChoiceDialog from "@/components/MarketChoiceDialog";
import FaqMarketToggle from "@/components/FaqMarketToggle";

const HERO_IMG = "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920";
const SERVICE_KEYS = ["residential", "deep", "move", "office", "post_reno", "window", "garden", "airbnb"];
export const BTN_GREEN = "inline-flex items-center justify-center px-6 h-12 rounded-full font-semibold text-white bg-[#5BA83F] hover:bg-[#4C9133] active:scale-[0.98] transition-[background-color,transform,box-shadow] shadow-md hover:shadow-[0_0_25px_rgba(91,168,63,0.35)]";
export const BTN_GHOST = "inline-flex items-center justify-center px-6 h-12 rounded-full font-semibold text-white bg-white/10 backdrop-blur border border-white/40 hover:bg-white/20 transition-colors";
const EYEBROW = "text-xs uppercase tracking-[0.22em] font-bold text-[#5BA83F]";
const H2 = "text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] leading-tight mt-3";

export default function Home() {
  const { t } = useTranslation();
  const [market, setMarket] = useState(null);
  return (
    <div data-testid="home-page">
      <MarketChoiceDialog mode={market} onClose={() => setMarket(null)} />
      <Hero t={t} pick={setMarket} />
      <ChooseCountry t={t} />
      <ServicesStrip t={t} />
      <HowItWorks t={t} />
      <WhyChoose t={t} />
      <Areas t={t} />
      <FaqPreview t={t} />
      <FinalCta t={t} pick={setMarket} />
    </div>
  );
}

function Hero({ t, pick }) {
  return (
    <section className="relative overflow-hidden min-h-[560px] md:min-h-[600px] lg:min-h-[680px] bg-slate-900">
      <img src={HERO_IMG} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#102547]/95 via-[#1B3A6B]/80 to-slate-900/40" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-14 sm:pt-20 lg:pt-28 pb-24 lg:pb-28 grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7 tl-reveal">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur text-[#9BD478] ring-1 ring-white/20 px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide" data-testid="hero-tag">
            <ShieldCheck className="w-4 h-4" /> {t("home_v3.eyebrow")}
          </div>
          <h1 className="mt-6 sm:mt-7 text-[2.2rem] min-[420px]:text-[2.5rem] sm:text-5xl md:text-[3.4rem] lg:text-6xl text-white leading-[1.08] md:leading-[1.05]" data-testid="home-hero-title">
            <span className="block [text-wrap:balance]">{t("home_v3.title_a")}</span>
            <span className="block italic text-[#9BD478] [text-wrap:balance]">{t("home_v3.title_b")}</span>
          </h1>
          <p className="mt-5 sm:mt-6 text-[15px] sm:text-lg text-slate-200/90 max-w-xl leading-relaxed">{t("home_v3.subtitle")}</p>
          <div className="mt-8 sm:mt-9 flex flex-col min-[420px]:flex-row flex-wrap gap-3 [&>*]:w-full min-[420px]:[&>*]:w-auto">
            <button type="button" onClick={() => pick("book")} className={BTN_GREEN} data-testid="global-hero-cta">{t("home_v3.cta_book")} <ArrowRight className="ml-2 w-4 h-4" /></button>
            <button type="button" onClick={() => pick("quote")} className={BTN_GHOST} data-testid="hero-quote-btn">{t("home_v3.cta_quote")}</button>
          </div>
          <div className="mt-10 sm:mt-12 grid grid-cols-3 gap-4 sm:gap-6 max-w-md">
            <Stat n="500+" l={t("hero.stat_homes")} /><Stat n="4.9★" l={t("hero.stat_rating")} /><Stat n="13" l={t("hero.stat_cities")} />
          </div>
        </div>
        <div className="hidden lg:block lg:col-span-5 tl-reveal" style={{ animationDelay: "150ms" }}>
          <div className="bg-white/10 backdrop-blur-xl ring-1 ring-white/20 rounded-3xl p-6 max-w-sm ml-auto shadow-2xl">
            <div className="flex gap-1 text-amber-300">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-4 h-4 fill-amber-300" />)}</div>
            <p className="mt-3 text-base text-white/95 leading-relaxed">"{t("hero.testimonial")}"</p>
            <p className="mt-3 text-xs text-slate-300">— Sophie V., The Hague</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ChooseCountry({ t }) {
  return (
    <section className="relative -mt-14 z-10 pb-14 md:pb-16">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="bg-white rounded-3xl shadow-[0_12px_40px_-4px_rgba(27,58,107,0.12)] ring-1 ring-slate-200/70 p-5 md:p-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className={EYEBROW}>{t("home_v3.choose_eyebrow")}</p>
              <h2 className={H2}>{t("home_v3.choose_title")}</h2>
            </div>
            <p className="text-sm text-slate-500 max-w-xs">{t("home_v3.choose_sub")}</p>
          </div>
          <div className="mt-6 grid md:grid-cols-2 gap-4 md:gap-5">
            <CountryCard testid="country-card-nl" to="/book" img={NL_IMAGE} badge={t("country.nl_badge")} badgeCls="bg-[#E8F5E4] text-[#3F7A1F]" icon={Zap}
              title={t("home_v3.nl_title")} desc={t("home_v3.nl_desc")} cta={t("home_v3.nl_cta")} ctaCls="text-[#5BA83F]" />
            <CountryCard testid="country-card-fr" to="/fr" img={RIVIERA_HERO} badge={t("country.fr_badge")} badgeCls="bg-sky-50 text-sky-700" icon={MessageSquareQuote}
              title={t("home_v3.fr_title")} desc={t("home_v3.fr_desc")} cta={t("home_v3.fr_cta")} ctaCls="text-[#0EA5E9]" />
          </div>
        </div>
      </div>
    </section>
  );
}

function CountryCard({ testid, to, img, badge, badgeCls, icon: Icon, title, desc, cta, ctaCls }) {
  return (
    <Link to={to} data-testid={testid} className="group relative rounded-2xl overflow-hidden ring-1 ring-slate-200 bg-white transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_-4px_rgba(27,58,107,0.15)]">
      <div className="h-40 md:h-44 lg:h-48 overflow-hidden">
        <img src={img} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      </div>
      <div className="p-5">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${badgeCls}`}><Icon className="w-3.5 h-3.5" /> {badge}</span>
        <h3 className="mt-3 text-2xl font-bold text-[#0F172A]">{title}</h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
        <span className={`mt-4 inline-flex items-center gap-1.5 text-sm font-bold ${ctaCls}`}>{cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>
      </div>
    </Link>
  );
}

function ServicesStrip({ t }) {
  return (
    <section className="py-20 md:py-28 bg-[#FAF9F6]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-12">
          <div><p className={EYEBROW}>{t("home.services_eyebrow")}</p><h2 className={H2}>{t("home.services_title")}</h2></div>
          <Link to="/services" className="text-sm font-semibold text-[#1B3A6B] hover:text-[#5BA83F]" data-testid="home-services-link">{t("home.services_view_all")}</Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SERVICE_KEYS.map((key, i) => (
            <div key={key} className="group bg-white rounded-2xl p-6 ring-1 ring-[#E5E0D8] hover:ring-[#1B3A6B]/30 transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_-4px_rgba(27,58,107,0.12)] tl-reveal"
              style={{ animationDelay: `${i * 50}ms` }} data-testid={`service-card-${key}`}>
              <div className="w-11 h-11 rounded-xl bg-[#1B3A6B]/5 flex items-center justify-center text-[#1B3A6B] mb-5 group-hover:bg-[#5BA83F]/15 group-hover:text-[#3F7A1F] transition-colors"><Sparkles className="w-5 h-5" /></div>
              <h3 className="font-bold text-[#0F172A]">{t(`services.${key}`)}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{t(`services.${key}_desc`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks({ t }) {
  const steps = [1, 2, 3].map((n) => ({ n: `0${n}`, title: t(`home.how_step${n}_title`), desc: t(`home.how_step${n}_desc`) }));
  return (
    <section className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4"><p className={EYEBROW}>{t("home.how_eyebrow")}</p><h2 className={H2}>{t("home.how_title")}</h2></div>
        <div className="lg:col-span-8 grid md:grid-cols-3 gap-5">
          {steps.map((s) => (
            <div key={s.n} className="relative rounded-2xl p-6 bg-white ring-1 ring-slate-200">
              <div className="font-display text-5xl text-[#5BA83F]/30">{s.n}</div>
              <h3 className="mt-3 text-lg font-bold text-[#0F172A]">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyChoose({ t }) {
  const items = [
    { icon: ShieldCheck, k: "vetted" }, { icon: BadgeCheck, k: "reclean" }, { icon: Clock, k: "ontime" }, { icon: Sparkles, k: "supplies" },
  ];
  return (
    <section className="py-20 md:py-28 bg-[#1B3A6B] text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-grain opacity-30" />
      <div className="max-w-7xl mx-auto px-5 md:px-8 relative grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#9BD478]">{t("home.why_eyebrow")}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight">{t("home.why_title")}</h2>
          <p className="mt-5 text-slate-300 leading-relaxed max-w-lg">{t("home.why_desc")}</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map(({ icon: Icon, k }) => (
            <div key={k} className="bg-white/5 ring-1 ring-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors">
              <Icon className="w-6 h-6 text-[#9BD478]" />
              <h4 className="mt-3 font-bold">{t(`home.why_${k}`)}</h4>
              <p className="mt-1 text-sm text-slate-300">{t(`home.why_${k}_desc`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Areas({ t }) {
  return (
    <section className="pt-12 pb-10 md:pt-16 md:pb-12 lg:pt-20 lg:pb-16" data-testid="home-service-areas">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <p className={EYEBROW}>{t("home.areas_eyebrow")}</p>
            <h2 className={H2} data-testid="home-areas-title">{t("home.areas_title")}</h2>
            <p className="mt-3 text-sm sm:text-base text-slate-600 max-w-xl" data-testid="home-areas-subtitle">{t("home.areas_subtitle")}</p>
          </div>
          <Link to="/service-areas" className="text-sm font-semibold text-[#1B3A6B] hover:text-[#5BA83F]" data-testid="home-areas-view-all">{t("home.areas_view_all")}</Link>
        </div>
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">{t("country.nl")}</p>
            <div className="flex flex-wrap gap-2">
              {NL_AREAS.map((c) => (
                <Link key={c.slug} to={`/service-areas/${c.slug}`} data-testid={`home-city-nl-${c.slug}`}><Chip label={c.city} /></Link>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">{t("country.fr")}</p>
            <div className="flex flex-wrap gap-2">
              {RIVIERA_CITIES.map((c) => (
                <Link key={c.slug} to={rivieraCityLink(c)} data-testid={`home-city-${c.slug}`}>
                  <Chip label={c.name} azure note={c.inquiryOnly ? t("riviera.inquiry_only") : null} />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Chip({ label, azure, note }) {
  return (
    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white ring-1 text-sm text-slate-700 transition-colors ${azure ? "ring-sky-200 hover:bg-sky-50" : "ring-slate-200 hover:bg-[#E8F5E4]/60"}`}>
      <MapPin className={`w-3.5 h-3.5 ${azure ? "text-[#0EA5E9]" : "text-[#5BA83F]"}`} /> {label}
      {note && <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-1.5 rounded-full">{note}</span>}
    </span>
  );
}

function FaqPreview({ t }) {
  const [market, setMarket] = useState("nl");
  const fr = market === "fr";
  return (
    <section className="py-12 md:py-16 lg:py-20 bg-[#F8FAFC]" data-testid="home-faq">
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <p className={`${EYEBROW} text-center`}>{t("home.faq_eyebrow")}</p>
        <h2 className={`${H2} text-center`}>{t("home.faq_title")}</h2>
        <div className="mt-6 flex justify-center"><FaqMarketToggle t={t} value={market} onChange={setMarket} testid="home-faq-market" /></div>
        <Accordion key={market} type="single" collapsible className="mt-6">
          {(t(fr ? "faqs_fr" : "faqs", { returnObjects: true }) || []).map((f, i) => (
            <AccordionItem key={i} value={`f-${i}`} data-testid="faq-accordion-item">
              <AccordionTrigger data-testid={`faq-${i + 1}`} className="text-left">{f.q}</AccordionTrigger>
              <AccordionContent>{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="text-center mt-8"><Link to={fr ? "/faq?country=fr" : "/faq"} className="text-sm font-semibold text-[#1B3A6B] hover:text-[#5BA83F]" data-testid="home-faq-view-all">{t("home.faq_view_all")}</Link></div>
      </div>
    </section>
  );
}

function FinalCta({ t, pick }) {
  return (
    <section className="pt-20 md:pt-28">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="bg-[#102547] rounded-3xl px-8 py-14 md:px-14 md:py-20 text-white relative overflow-hidden">
          <div className="absolute -right-16 -top-16 w-72 h-72 bg-[#5BA83F]/25 rounded-full blur-3xl" />
          <div className="absolute -left-10 bottom-0 w-60 h-60 bg-[#0EA5E9]/20 rounded-full blur-3xl" />
          <div className="relative">
            <h2 className="text-3xl md:text-5xl leading-tight max-w-2xl">{t("home.cta_title")}</h2>
            <p className="mt-4 text-slate-300 max-w-xl">{t("home.cta_subtitle")}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button type="button" onClick={() => pick("book")} className={BTN_GREEN} data-testid="home-cta-book">{t("home_v3.cta_book")}</button>
              <button type="button" onClick={() => pick("quote")} className={BTN_GHOST} data-testid="home-cta-quote">{t("home_v3.cta_quote")}</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-display text-3xl md:text-4xl text-white">{n}</div>
      <div className="text-xs text-slate-300 mt-0.5">{l}</div>
    </div>
  );
}
